import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { copyToClipboard } from "@/lib/utils";
import { Copy, Trash2 } from "lucide-react";
import CryptoJS from "crypto-js";
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function HashGenerator() {
  const [input, setInput] = useState("");
  const [hashes, setHashes] = useState({
    md5: "",
    sha1: "",
    sha256: "",
    sha512: "",
  });
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!input) {
      setHashes({ md5: "", sha1: "", sha256: "", sha512: "" });
      return;
    }
    setHashes({
      md5: CryptoJS.MD5(input).toString(),
      sha1: CryptoJS.SHA1(input).toString(),
      sha256: CryptoJS.SHA256(input).toString(),
      sha512: CryptoJS.SHA512(input).toString(),
    });
  }, [input]);

  const handleCopy = (hashType: string, value: string) => {
    copyToClipboard(value).then(() => {
      setCopiedStates((prev) => ({ ...prev, [hashType]: true }));
      setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [hashType]: false }));
      }, 2000);
    });
  };

  return (
    <ToolPageLayout
      title="Online Hash Generator – Generate MD5, SHA256, SHA512"
      description="Generate secure hashes like MD5, SHA-1, SHA-256, and SHA-512 instantly with our free online hash generator."
      h1="Hash Generator"
      category="JWT & Security"
      categoryPath="/jwt-security"
      howToUse={[
        "Paste your text in the input area.",
        "The tool will automatically generate MD5, SHA1, SHA256, and SHA512 hashes.",
        "Copy your desired hash."
      ]}
      faq={[
        { question: "What is an online hash generator and how does it work?", answer: "An online hash generator is a web-based utility that takes any string input and processes it through standard mathematical algorithms (such as MD5, SHA-1, SHA-256, and SHA-512) to produce a unique, fixed-length hexadecimal digest representing that specific input." },
        { question: "What is a cryptographic hash function?", answer: "A cryptographic hash function is an algorithm that maps arbitrary-sized input data to a fixed-size bit array. It is designed to be a one-way function—meaning it is extremely easy to calculate the hash for any given data, but virtually impossible to reverse the process." },
        { question: "What is the key difference between Hashing and Encryption?", answer: "Hashing is a strictly one-way operation intended to verify data integrity and authenticity; it cannot be decrypted. Encryption is a two-way operation designed to conceal data confidentiality; it can be decrypted back to plain text using the correct cryptographic key." },
        { question: "How is a SHA-256 hash generated and why is it considered secure?", answer: "SHA-256 (Secure Hash Algorithm 256-bit) breaks input data into 512-bit blocks, applies 64 rounds of complex logical operations (like bitwise shifts, ANDs, and XORs), and outputs a 256-bit (64-character hex) digest. It is secure because no collision has ever been found, and it is immune to practical brute-force reversal." },
        { question: "What is the MD5 algorithm and is it still safe for production security?", answer: "MD5 produces a 128-bit digest. While popular historically for file integrity, MD5 is no longer safe for modern production security, password hashing, or digital signatures due to severe 'collision vulnerabilities' where attackers can construct different files that yield the identical MD5 hash." },
        { question: "What does a 'hash collision' mean and why is it dangerous?", answer: "A hash collision occurs when two completely distinct inputs yield the exact same hash output. This is dangerous because an attacker could substitute a benign file (like a contract or software update) with a malicious version that retains the same valid hash fingerprint, bypassing security checks." },
        { question: "Can a generated hash checksum be decrypted back to plain text?", answer: "No. Modern cryptographic hashes are designed as one-way trapdoor mathematical functions. There is no decryption key or reverse algorithm. The only way to find the original input is to guess it (brute force) or use pre-calculated databases of common hashes (rainbow tables)." },
        { question: "Why are cryptographic hashes critical for securely storing user passwords?", answer: "Instead of storing passwords in plain text—which would be catastrophic if a database is breached—servers generate and store a secure hash of the password (usually combined with a unique, random value called a 'salt'). When logging in, the server hashes your input and matches it with the stored hash." },
        { question: "Is my private text safe when pasted into this online hash generator?", answer: "Yes, this online hash generator is completely safe. It executes all hashing calculations locally within your web browser using client-side JavaScript. No input data or calculated digests are ever transmitted over the network or saved to external databases." },
        { question: "What are the core differences between SHA-1, SHA-256, and SHA-512?", answer: "SHA-1 produces a 160-bit digest and is now legacy and insecure. SHA-256 produces a 256-bit (64-character hex) digest and is the standard for most modern web security. SHA-512 produces a larger 512-bit (128-character hex) digest and is optimized for 64-bit processors, offering the highest collision resistance." }
      ]}
    >
      <div className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium text-slate-700">Input Text</label>
          <Textarea
            placeholder="Type string to hash..."
            className="min-h-[150px]"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
        </div>

        <div className="space-y-4">
          {Object.entries(hashes).map(([type, value]) => (
            <div key={type} className="space-y-1">
              <label className="text-sm font-medium text-slate-700 uppercase">{type}</label>
              <div className="flex gap-2">
                <Input value={value} readOnly className="font-mono bg-slate-50 text-slate-600" />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => handleCopy(type, value as string)}
                  disabled={!value}
                  title="Copy hash"
                  aria-label={`Copy ${type} hash to clipboard`}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              {copiedStates[type] && <span className="text-xs text-green-600">Copied to clipboard!</span>}
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Button onClick={() => setInput("")} variant="ghost" className="gap-2 text-red-600 hover:text-red-700 hover:bg-red-50">
            <Trash2 className="h-4 w-4" />
            Clear Input
          </Button>
        </div>
      </div>
    </ToolPageLayout>
  );
}
